import type { QueryClient } from '@tanstack/react-query';
import { ELocalStorageKeys } from '@web-store-platforms/types';
import { webStorageUtil } from '@/lib/storage/webStorageUtil';
import { API_MOCK_QUERY_KEYS, API_MOCK_STORAGE_KEYS, DEV_API_MOCK_COOKIE_VALUE } from './api-mock.consts';
import { isApiMockEnvironmentAllowed } from './api-mock.env';

export { isApiMockEnvironmentAllowed, shouldSkipShopPrefetch } from './api-mock.env';

type ApiMockPanelOpener = () => void;

let apiMockPanelOpener: ApiMockPanelOpener | null = null;

const API_MOCK_ENTRIES: ReadonlyArray<{
    storageKey: ELocalStorageKeys;
    queryKey: readonly string[];
}> = [
    { storageKey: API_MOCK_STORAGE_KEYS.boot, queryKey: API_MOCK_QUERY_KEYS.boot },
    { storageKey: API_MOCK_STORAGE_KEYS.offers, queryKey: API_MOCK_QUERY_KEYS.offers },
    { storageKey: API_MOCK_STORAGE_KEYS.template, queryKey: API_MOCK_QUERY_KEYS.template },
];

export const getApiMock = <T>(key: ELocalStorageKeys): T | null => {
    if (!isApiMockEnvironmentAllowed()) {
        return null;
    }
    const value = webStorageUtil.get(key);
    if (value === null || value === undefined) {
        return null;
    }
    return value as T;
};

export const hasAnyApiMock = (): boolean => {
    if (!isApiMockEnvironmentAllowed()) {
        return false;
    }
    return API_MOCK_ENTRIES.some((entry) => getApiMock(entry.storageKey) !== null);
};

export const applyApiMocksToQueryClient = (queryClient: QueryClient): void => {
    API_MOCK_ENTRIES.forEach((entry) => {
        const mock = getApiMock(entry.storageKey);
        if (mock !== null) {
            queryClient.setQueryData(entry.queryKey, mock);
        }
    });
};

export const setDevApiMockCookie = (): void => {
    if (typeof document === 'undefined') {
        return;
    }
    if (!isApiMockEnvironmentAllowed()) {
        return;
    }
    document.cookie = `${ELocalStorageKeys.DEV_API_MOCK_COOKIE}=${DEV_API_MOCK_COOKIE_VALUE}; path=/`;
};

export const clearDevApiMockCookie = (): void => {
    if (typeof document === 'undefined') {
        return;
    }
    document.cookie = `${ELocalStorageKeys.DEV_API_MOCK_COOKIE}=; path=/; max-age=0`;
};

export const syncDevApiMockCookie = (): void => {
    if (typeof document === 'undefined') {
        return;
    }
    if (!isApiMockEnvironmentAllowed()) {
        return;
    }
    if (hasAnyApiMock()) {
        setDevApiMockCookie();
        return;
    }
    clearDevApiMockCookie();
};

export const registerApiMockPanelOpener = (opener: ApiMockPanelOpener | null): void => {
    apiMockPanelOpener = opener;
};

export const openApiMockPanel = (): void => {
    if (!isApiMockEnvironmentAllowed()) {
        return;
    }
    if (apiMockPanelOpener === null) {
        return;
    }
    apiMockPanelOpener();
};
